import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import { selectMode } from '../../app/appSlice';
import BookCard from './BookCard';
import Title from '../Title';
import { Container } from 'react-bootstrap';
import { Element } from 'react-scroll';

const Wrapper = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem 1.5rem;
`;

const Carousel = styled.div`
  display: flex;
  gap: 1.5rem;
  justify-content: center;
  flex-wrap: wrap;
  padding: 0.5rem 0;
  margin-top: 1.5rem;

  @media (max-width: 576px) {
    gap: 1rem;
  }
`;

const DetailPanel = styled.div`
  overflow: hidden;
  transition: max-height 0.45s cubic-bezier(0.22, 1, 0.36, 1),
              opacity 0.35s ease;
  max-height: ${({ $isOpen }) => ($isOpen ? '600px' : '0')};
  opacity: ${({ $isOpen }) => ($isOpen ? 1 : 0)};
`;

const DetailInner = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-top: 2rem;
  padding: 1.75rem;
  border-radius: 10px;
  text-align: left;
  background: ${({ $theme }) =>
    $theme === 'light' ? '#f5f5f5' : '#333'};

  @media (min-width: 768px) {
    flex-direction: row;
    gap: 2rem;
  }
`;

const DetailCover = styled.div`
  flex-shrink: 0;
  display: flex;
  justify-content: center;

  img {
    width: 140px;
    height: auto;
    border-radius: 6px;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.18);
  }

  @media (min-width: 768px) {
    img {
      width: 120px;
    }
  }
`;

const DetailContent = styled.div`
  flex-grow: 1;
  min-width: 0;

  h3 {
    font-size: 1.2rem;
    font-weight: 600;
    margin-bottom: 0.15rem;
    color: ${({ $theme }) =>
      $theme === 'light' ? '#222' : '#f0f0f0'};
  }

  .author {
    font-style: italic;
    font-size: 0.9rem;
    color: ${({ $theme }) =>
      $theme === 'light' ? '#666' : '#bbb'};
    margin-bottom: 0.75rem;
  }

  .intro {
    font-size: 0.92rem;
    line-height: 1.6;
    color: ${({ $theme }) =>
      $theme === 'light' ? '#444' : '#ccc'};
    margin-bottom: 1rem;
  }

  .takeaway-label {
    display: inline-block;
    font-size: 0.7rem;
    font-weight: 600;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: ${({ $theme }) =>
      $theme === 'light' ? '#888' : '#999'};
    margin-bottom: 0.35rem;
  }

  .takeaway {
    font-size: 0.88rem;
    line-height: 1.7;
    color: ${({ $theme }) =>
      $theme === 'light' ? '#555' : '#bbb'};
    font-style: italic;
    margin: 0;
  }
`;

const BooksSection = ({ books }) => {
  const theme = useSelector(selectMode);
  const [activeIndex, setActiveIndex] = useState(null);
  const detailRef = useRef(null);

  const handleClick = (index) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  // Scroll detail panel into view when opened
  useEffect(() => {
    if (activeIndex !== null && detailRef.current) {
      // Small delay to let the animation start
      const timer = setTimeout(() => {
        detailRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [activeIndex]);

  const activeBook = activeIndex !== null ? books[activeIndex] : null;

  return (
    <Element name="Favourite Books" id="books">
      <section className="section">
        <Container fluid>
          <Wrapper>
            <Container className="text-center">
              <Title size={"h2"} text={"Favourite Books"} />
            </Container>
            <Carousel>
              {books.map((book, index) => (
                <BookCard
                  key={index}
                  title={book.title}
                  author={book.author}
                  coverUrl={book.coverUrl}
                  isActive={activeIndex === index}
                  onClick={() => handleClick(index)}
                  theme={theme}
                />
              ))}
            </Carousel>
            <DetailPanel $isOpen={activeBook !== null} ref={detailRef}>
              {activeBook && (
                <DetailInner $theme={theme}>
                  <DetailCover>
                    <img src={activeBook.coverUrl} alt={`Cover of ${activeBook.title}`} />
                  </DetailCover>
                  <DetailContent $theme={theme}>
                    <h3>{activeBook.title}</h3>
                    <p className="author">{activeBook.author}</p>
                    <p className="intro">{activeBook.intro}</p>
                    {activeBook.takeaway && (
                      <>
                        <span className="takeaway-label">My Takeaway</span>
                        <p className="takeaway">{activeBook.takeaway}</p>
                      </>
                    )}
                  </DetailContent>
                </DetailInner>
              )}
            </DetailPanel>
          </Wrapper>
        </Container>
      </section>
    </Element>
  );
};

export default BooksSection;
